import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Image, Rect } from 'react-konva';
import useImage from 'use-image';
import { TEXT_ELEMENT_CONSTANTS } from '@/components/Board/TextEditor/constants';
import { useTiptapTextRenderer } from '@/hooks/board/useTiptapTextRenderer';
import { mapStrokeWidth } from '@/utils/board/strokeUtils';
const { DEFAULT_ELEMENT_WIDTH, DEFAULT_ELEMENT_HEIGHT, DEFAULT_STROKE_WIDTH } = TEXT_ELEMENT_CONSTANTS;
/**
 * Компонент текстовой группы
 * Текст не растягивается визуально при трансформировании и не влияет на bounding box трансформера
 */
const TextGroupComponent = ({ el, showElementSelectionBorder, layerLocked, common, onDoubleClick, zoom = 1, isVisible = true, isPresentationMode = false, }) => {
    var _a, _b, _c, _d, _e, _f;
    const parentGroupRef = useRef(null);
    const textGroupRef = useRef(null);
    const imageRef = useRef(null);
    const rerenderTextOnTransformRef = useRef(null);
    // Базовые размеры и свойства
    const baseDimensions = useMemo(() => {
        var _a, _b;
        return ({
            width: ((_a = el.props) === null || _a === void 0 ? void 0 : _a.w) || DEFAULT_ELEMENT_WIDTH,
            height: ((_b = el.props) === null || _b === void 0 ? void 0 : _b.h) || DEFAULT_ELEMENT_HEIGHT,
        });
    }, [(_a = el.props) === null || _a === void 0 ? void 0 : _a.w, (_b = el.props) === null || _b === void 0 ? void 0 : _b.h]);
    const textDimensions = useMemo(() => ({
        width: baseDimensions.width,
        height: baseDimensions.height,
    }), [baseDimensions]);
    /**
     * Вычисляет оптимальный scale для рендеринга текста в изображение.
     * Использует разные уровни качества в зависимости от zoom для баланса между качеством и производительностью.
     *
     * @returns scale для html2canvas
     */
    const renderScale = useMemo(() => {
        const devicePixelRatio = window.devicePixelRatio || 1;
        const minScale = devicePixelRatio * 2; // Для zoom < 1 (уменьшение)
        const midScale = devicePixelRatio * 3; // Для 1 <= zoom <= 5 (обычный вид)
        const maxScale = devicePixelRatio * 4; // Для zoom > 5 (сильное увеличение)
        if (zoom < 1)
            return minScale;
        if (zoom > 5)
            return maxScale;
        return midScale;
    }, [zoom]);
    const strokeConfig = useMemo(() => {
        var _a, _b, _c;
        const stroke = typeof ((_a = el.props) === null || _a === void 0 ? void 0 : _a.stroke) === 'string' && el.props.stroke ? el.props.stroke : undefined;
        const logicalStrokeWidth = typeof ((_b = el.props) === null || _b === void 0 ? void 0 : _b.strokeWidth) === 'number' && !isNaN(el.props.strokeWidth)
            ? el.props.strokeWidth
            : 5; // Значение по умолчанию 5 (маппится в 1)
        // Применяем маппинг для физического размера обводки
        const strokeWidth = mapStrokeWidth(logicalStrokeWidth);
        let strokeEnabled = false;
        if (isPresentationMode && el.isPresentationFrame) {
            strokeEnabled = false;
        }
        else {
            strokeEnabled =
                typeof ((_c = el.props) === null || _c === void 0 ? void 0 : _c.strokeEnabled) === 'boolean'
                    ? el.props.strokeEnabled
                    : stroke !== undefined && stroke !== null;
        }
        return { stroke, strokeWidth, strokeEnabled };
    }, [
        (_c = el.props) === null || _c === void 0 ? void 0 : _c.stroke,
        (_d = el.props) === null || _d === void 0 ? void 0 : _d.strokeWidth,
        (_e = el.props) === null || _e === void 0 ? void 0 : _e.strokeEnabled,
        isPresentationMode,
        el.isPresentationFrame,
    ]);
    // Отслеживаем текущий scale для обновления размеров рендеринга при трансформации
    const [currentScaleX, setCurrentScaleX] = useState(1);
    const [currentScaleY, setCurrentScaleY] = useState(1);
    // Вычисляем размеры для рендеринга с учетом трансформации
    const renderWidth = useMemo(() => {
        const scaleX = currentScaleX || 1;
        return baseDimensions.width * scaleX || textDimensions.width;
    }, [baseDimensions.width, textDimensions.width, currentScaleX]);
    const renderHeight = useMemo(() => {
        const scaleY = currentScaleY || 1;
        return baseDimensions.height * scaleY || textDimensions.height;
    }, [baseDimensions.height, textDimensions.height, currentScaleY]);
    // Используем хук для рендеринга tiptap контента в изображение
    const { url: imageUrl, size: imageSize } = useTiptapTextRenderer({
        element: el,
        width: renderWidth,
        height: renderHeight,
        renderScale,
    });
    const [renderedImage] = useImage(imageUrl || '', 'anonymous');
    /**
     * Обновляет масштаб текстового изображения при трансформации элемента.
     * Компенсирует масштабирование родительской группы, чтобы текст не растягивался визуально.
     */
    const updateTextScale = useCallback(() => {
        var _a;
        if (!parentGroupRef.current || !textGroupRef.current || !imageRef.current || !imageSize) {
            return;
        }
        const groupScaleX = parentGroupRef.current.scaleX();
        const groupScaleY = parentGroupRef.current.scaleY();
        // Устанавливаем размеры и позицию изображения
        imageRef.current.width(imageSize.width);
        imageRef.current.height(imageSize.height);
        imageRef.current.x(0);
        imageRef.current.y(0);
        // Компенсируем масштабирование родительской группы
        if (groupScaleX !== 0 && groupScaleY !== 0) {
            textGroupRef.current.scaleX(1 / groupScaleX);
            textGroupRef.current.scaleY(1 / groupScaleY);
        }
        else {
            textGroupRef.current.scaleX(1);
            textGroupRef.current.scaleY(1);
        }
        (_a = imageRef.current.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
    }, [imageSize]);
    /**
     * Перерендеривает текст при трансформации элемента (изменение размера).
     * Вычисляет новые размеры на основе масштаба родительской группы.
     * Изменение высоты влияет на размер текста (масштабирование шрифта).
     */
    const rerenderTextOnTransform = useCallback(() => {
        if (!parentGroupRef.current)
            return;
        const groupScaleX = parentGroupRef.current.scaleX() || 1;
        const groupScaleY = parentGroupRef.current.scaleY() || 1;
        setCurrentScaleX(groupScaleX);
        setCurrentScaleY(groupScaleY);
    }, []);
    useEffect(() => {
        rerenderTextOnTransformRef.current = rerenderTextOnTransform;
    }, [rerenderTextOnTransform]);
    useEffect(() => {
        const parentGroup = parentGroupRef.current;
        if (!parentGroup)
            return;
        let rerenderInterval = null;
        let isTransforming = false;
        const handleTransform = () => {
            updateTextScale();
            if (!rerenderInterval) {
                isTransforming = true;
                rerenderInterval = setInterval(() => {
                    var _a;
                    if (isTransforming) {
                        (_a = rerenderTextOnTransformRef.current) === null || _a === void 0 ? void 0 : _a.call(rerenderTextOnTransformRef);
                    }
                }, 100);
            }
        };
        const handleTransformEnd = () => {
            var _a;
            isTransforming = false;
            if (rerenderInterval) {
                clearInterval(rerenderInterval);
                rerenderInterval = null;
            }
            (_a = rerenderTextOnTransformRef.current) === null || _a === void 0 ? void 0 : _a.call(rerenderTextOnTransformRef);
            updateTextScale();
            // Обновляем трансформер после изменения размеров текстового элемента
            // Это необходимо, так как размеры элемента могут измениться после перерендеринга текста
            requestAnimationFrame(() => {
                var _a, _b;
                const transformer = (_a = parentGroup.getStage()) === null || _a === void 0 ? void 0 : _a.findOne('Transformer');
                if (transformer) {
                    transformer.forceUpdate();
                    (_b = transformer.getLayer()) === null || _b === void 0 ? void 0 : _b.batchDraw();
                }
            });
        };
        parentGroup.on('transform', handleTransform);
        parentGroup.on('transformend', handleTransformEnd);
        if (renderedImage && imageSize) {
            updateTextScale();
        }
        return () => {
            parentGroup.off('transform', handleTransform);
            parentGroup.off('transformend', handleTransformEnd);
            if (rerenderInterval) {
                clearInterval(rerenderInterval);
            }
        };
    }, [updateTextScale, renderedImage, imageSize]);
    useEffect(() => {
        const parentGroup = parentGroupRef.current;
        if (!parentGroup)
            return;
        const originalGetClientRect = parentGroup.getClientRect.bind(parentGroup);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parentGroup.getClientRect = function (config) {
            const children = this.children || [];
            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;
            for (const child of children) {
                if (child === textGroupRef.current)
                    continue;
                const box = child.getClientRect(config);
                minX = Math.min(minX, box.x);
                minY = Math.min(minY, box.y);
                maxX = Math.max(maxX, box.x + box.width);
                maxY = Math.max(maxY, box.y + box.height);
            }
            if (minX === Infinity) {
                const absPos = this.absolutePosition();
                const scaleX = this.scaleX() || 1;
                return {
                    x: absPos.x,
                    y: absPos.y,
                    width: baseDimensions.width * scaleX,
                    height: baseDimensions.height * scaleX,
                };
            }
            return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        };
        return () => {
            if (parentGroup.getClientRect) {
                parentGroup.getClientRect = originalGetClientRect;
            }
        };
    }, [baseDimensions]);
    const handleDoubleClick = useCallback((e) => {
        onDoubleClick === null || onDoubleClick === void 0 ? void 0 : onDoubleClick(e, el.id);
    }, [onDoubleClick, el.id]);
    const clipFunc = useCallback((ctx) => {
        var _a, _b;
        const groupScaleX = ((_a = parentGroupRef.current) === null || _a === void 0 ? void 0 : _a.scaleX()) || 1;
        const groupScaleY = ((_b = parentGroupRef.current) === null || _b === void 0 ? void 0 : _b.scaleY()) || 1;
        const newWidth = baseDimensions.width * groupScaleX;
        const newHeight = baseDimensions.height * groupScaleY;
        ctx.rect(0, 0, newWidth, newHeight);
    }, [baseDimensions]);
    return (_jsxs(Group, Object.assign({ ref: parentGroupRef }, common, { visible: isVisible, children: [_jsx(Rect, { x: 0, y: 0, width: baseDimensions.width, height: baseDimensions.height, fill: ((_f = el.props) === null || _f === void 0 ? void 0 : _f.backgroundColor) || 'transparent', stroke: strokeConfig.strokeEnabled ? strokeConfig.stroke : undefined, strokeWidth: strokeConfig.strokeEnabled ? strokeConfig.strokeWidth : undefined, strokeScaleEnabled: true, listening: true, onDblClick: handleDoubleClick, perfectDrawEnabled: false }), _jsx(Group, { ref: textGroupRef, x: 0, y: 0, scaleX: 1, scaleY: 1, clipFunc: clipFunc, children: renderedImage && imageSize && (_jsx(Image, { ref: imageRef, image: renderedImage, x: 0, y: 0, width: imageSize.width, height: imageSize.height, onDblClick: handleDoubleClick, perfectDrawEnabled: false, listening: false })) }), showElementSelectionBorder && (_jsx(Rect, { x: 0, y: 0, width: baseDimensions.width, height: baseDimensions.height, stroke: el.locked || layerLocked ? '#d33' : '#5eafffff', strokeWidth: DEFAULT_STROKE_WIDTH, strokeScaleEnabled: false, listening: false, shadowForStrokeEnabled: false, perfectDrawEnabled: false }))] }), el.id));
};
// Мемоизированный компонент
const MemoizedTextGroupComponent = memo(TextGroupComponent, (prevProps, nextProps) => {
    const prevEl = prevProps.el;
    const nextEl = nextProps.el;
    const prevProps_el = prevEl.props;
    const nextProps_el = nextEl.props;
    return (prevEl.id === nextEl.id &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.text) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.text) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.w) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.w) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.h) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.h) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.fontSize) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.fontSize) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.fontFamily) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.fontFamily) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.fontStyle) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.fontStyle) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.textDecoration) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.textDecoration) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.textAlign) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.textAlign) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.verticalAlign) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.verticalAlign) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.textColor) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.textColor) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.backgroundColor) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.backgroundColor) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.stroke) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.stroke) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.strokeWidth) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.strokeWidth) &&
        (prevProps_el === null || prevProps_el === void 0 ? void 0 : prevProps_el.strokeEnabled) === (nextProps_el === null || nextProps_el === void 0 ? void 0 : nextProps_el.strokeEnabled) &&
        prevEl.locked === nextEl.locked &&
        prevProps.showElementSelectionBorder === nextProps.showElementSelectionBorder &&
        prevProps.layerLocked === nextProps.layerLocked &&
        prevProps.zoom === nextProps.zoom &&
        prevProps.isVisible === nextProps.isVisible);
});
export function textGroup({ el, showElementSelectionBorder, layerLocked, common, clipFunc: _clipFunc, onDoubleClick, zoom, isVisible = true, isPresentationMode = false, }) {
    return (_jsx(MemoizedTextGroupComponent, { el: el, showElementSelectionBorder: showElementSelectionBorder, layerLocked: layerLocked, common: common, onDoubleClick: onDoubleClick, zoom: zoom, isVisible: isVisible, isPresentationMode: isPresentationMode }));
}
